import type { ReactNode } from 'react';
import { ChevronDownIcon } from '@/components/icons';
import type { ReferenceRef } from '@/features/portal/types';
import { dayTone, formatDays, STATE_HINTS } from '../timesheet-labels';
import type { Timesheet, TimesheetStateCode } from '../types';

export const TimesheetSummaryPanel = ({
  timesheet,
  dayTypes,
  actions,
  pending,
}: {
  timesheet: Timesheet;
  dayTypes: ReferenceRef[];
  actions: ReactNode;
  pending: boolean;
}) => {
  const work = timesheet.days.filter((day) => day.dayType.code === 'TRAVAILLE');
  const types = [
    ...new Map(
      [...dayTypes, ...timesheet.days.map((day) => day.dayType)].map((type) => [type.code, type]),
    ).values(),
  ];
  return (
    <aside className="cra-summary" aria-label="Suivi du compte rendu">
      <div className="cra-summary__section">
        <span className="cra-eyebrow">Ce mois-ci</span>
        <h2>Récapitulatif</h2>
        <dl className="cra-summary__totals">
          <div>
            <dt>Jours travaillés</dt>
            <dd>
              {formatDays(timesheet.totals.workedDays)} <small>j</small>
            </dd>
          </div>
          <div>
            <dt>Absences déclarées</dt>
            <dd>
              {formatDays(timesheet.totals.absenceDays)} <small>j</small>
            </dd>
          </div>
          <div>
            <dt>Heures supplémentaires</dt>
            <dd>
              {formatDays(timesheet.totals.overtimeHours)} <small>h</small>
            </dd>
          </div>
        </dl>
        <p className="cra-summary__hint">
          {timesheet.totals.expectedDays} jours ouvrés sur la période de mission.
        </p>
      </div>
      <details className="cra-summary__details">
        <summary>
          Détail des journées <ChevronDownIcon />
        </summary>
        <dl className="cra-summary__breakdown">
          <div>
            <dt>
              <i className={dayTone('TRAVAILLE')} />
              Journées complètes
            </dt>
            <dd>{work.filter((day) => Number(day.value) === 1).length}</dd>
          </div>
          <div>
            <dt>
              <i className={dayTone('TRAVAILLE')} />
              Demi-journées
            </dt>
            <dd>{work.filter((day) => Number(day.value) === 0.5).length}</dd>
          </div>
          {types
            .filter(
              (type) =>
                type.code !== 'TRAVAILLE' && Number(timesheet.totals.byDayType[type.code] ?? 0) > 0,
            )
            .map((type) => (
              <div key={type.code}>
                <dt>
                  <i className={dayTone(type.code)} />
                  {type.label}
                </dt>
                <dd>
                  {formatDays(timesheet.totals.byDayType[type.code] ?? '0')} <small>j</small>
                </dd>
              </div>
            ))}
        </dl>
      </details>
      <div className="cra-summary__validation">
        <p>
          {pending
            ? 'Enregistrement en cours…'
            : STATE_HINTS[timesheet.state.code as TimesheetStateCode]}
        </p>
        {actions}
      </div>
    </aside>
  );
};
